/**
 * Promesas de cobro (cobros 'Pendiente de Cobro'): vencidas / por vencer y el
 * resumen por caja × mes de Cuentas por Cobrar. Pedido del cliente 2026-10-20.
 *
 * Fechas calendario 'YYYY-MM-DD' de Argentina (hoy = hoyAr()), sin horas.
 * Self-check: npx tsx src/features/administracion/lib/promesas.selfcheck.ts
 */

/** "Por vencer" = vence hoy o en los próximos N días corridos. */
export const POR_VENCER_DIAS = 3;

export type EstadoPromesa = 'vencida' | 'por_vencer' | 'futura';

const ISO = /^(\d{4})-(\d{2})-(\d{2})/;

/** Días de `hoy` a `fecha` (0 = hoy, negativo = pasó). null si alguna fecha no es válida. */
export function diasHasta(fecha: string | null | undefined, hoy: string): number | null {
  const f = ISO.exec(fecha ?? '');
  const h = ISO.exec(hoy);
  if (!f || !h) return null;
  const utc = (m: RegExpExecArray) => Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.round((utc(f) - utc(h)) / 86_400_000);
}

export function estadoPromesa(dias: number): EstadoPromesa {
  if (dias < 0) return 'vencida';
  return dias <= POR_VENCER_DIAS ? 'por_vencer' : 'futura';
}

/** "vence hoy" / "vence en 2 días" / "vencida hace 5 días". */
export function textoDias(dias: number): string {
  if (dias === 0) return 'vence hoy';
  if (dias > 0) return `vence en ${dias} día${dias === 1 ? '' : 's'}`;
  return `vencida hace ${-dias} día${dias === -1 ? '' : 's'}`;
}

/** 'dd/mm/yyyy' (como vienen los campos *_CP) → 'YYYY-MM-DD'; null si no parsea. */
export function ddmmyyyyAIso(v: string | null | undefined): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec((v ?? '').trim());
  return m ? `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}` : null;
}

/** Columna del resumen: 'vencido', un mes 'YYYY-MM' o 'sin_fecha'. */
export function columnaPromesa(fecha: string | null, hoy: string): string {
  const d = diasHasta(fecha, hoy);
  if (d == null) return 'sin_fecha';
  return d < 0 ? 'vencido' : fecha!.slice(0, 7);
}

/** 'vencido' primero, los meses en orden, 'sin_fecha' al final; sin repetidos. */
export function ordenarColumnas(cols: Iterable<string>): string[] {
  const rango = (c: string) => (c === 'vencido' ? '0' : c === 'sin_fecha' ? '2' : `1${c}`);
  return [...new Set(cols)].sort((a, b) => rango(a).localeCompare(rango(b)));
}

export type PromesaResumible = {
  medio: string;
  moneda: string;
  monto: number;
  /** 'YYYY-MM-DD' o null. */
  fecha: string | null;
};

export type ResumenFila = { medio: string; porColumna: Record<string, number>; total: number };
export type ResumenMoneda = {
  moneda: string;
  filas: ResumenFila[];
  totalPorColumna: Record<string, number>;
  total: number;
};
export type ResumenMedioMes = { columnas: string[]; monedas: ResumenMoneda[] };

/**
 * Suma por caja × columna, un bloque por moneda (no se convierte: el monto está
 * en la moneda de la caja). Columnas: 'vencido' primero, después los meses en
 * orden, 'sin_fecha' al final; sólo las que tienen algo.
 */
export function resumenMedioMes(rows: PromesaResumible[], hoy: string): ResumenMedioMes {
  const cols = new Set<string>();
  const porMoneda = new Map<string, Map<string, ResumenFila>>();
  for (const r of rows) {
    const col = columnaPromesa(r.fecha, hoy);
    cols.add(col);
    const filas = porMoneda.get(r.moneda) ?? new Map<string, ResumenFila>();
    porMoneda.set(r.moneda, filas);
    const fila = filas.get(r.medio) ?? { medio: r.medio, porColumna: {}, total: 0 };
    filas.set(r.medio, fila);
    fila.porColumna[col] = (fila.porColumna[col] ?? 0) + r.monto;
    fila.total += r.monto;
  }
  const columnas = ordenarColumnas(cols);
  const monedas = [...porMoneda.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([moneda, filasMap]) => {
      const filas = [...filasMap.values()].sort((a, b) => b.total - a.total);
      const totalPorColumna: Record<string, number> = {};
      for (const f of filas) {
        for (const [c, v] of Object.entries(f.porColumna)) totalPorColumna[c] = (totalPorColumna[c] ?? 0) + v;
      }
      return { moneda, filas, totalPorColumna, total: filas.reduce((a, f) => a + f.total, 0) };
    });
  return { columnas, monedas };
}

/** Encabezado de columna: 'Vencido', 'Oct 2026', 'Sin fecha'. */
export function etiquetaColumna(col: string): string {
  if (col === 'vencido') return 'Vencido';
  if (col === 'sin_fecha') return 'Sin fecha';
  const [anio, mes] = col.split('-');
  const nombre = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][Number(mes) - 1];
  return `${nombre ?? mes} ${anio}`;
}
